import { useState } from "react";
import { forgotPassword } from "./api/apiFunctions";

export default function ForgotPasswordModal({ open, onClose }) {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const handleClose = () => {
    setEmail("");
    setError("");
    setSubmitted(false);
    setIsLoading(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    setIsLoading(true);
    try {
      await forgotPassword(email.toLowerCase().trim());
      setSubmitted(true);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl border border-[#e5e7eb] w-11/12 sm:max-w-md p-8 relative">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl focus:outline-none"
          aria-label="Close"
        >
          ×
        </button>

        {!submitted ? (
          <>
            <h2 className="text-2xl font-extrabold text-[#2d348f] mb-2">Forgot Password</h2>
            <p className="text-sm text-[#837f90] mb-6">
              Enter the email address associated with your account and we'll send you a link to reset your password.
            </p>
            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-[#837f90]">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  disabled={isLoading}
                  className="border-0 border-b-2 border-[#4962d8] focus:outline-none text-sm py-1 bg-transparent text-black font-medium disabled:opacity-50"
                />
              </div>
              {error && <div className="text-red-500 text-sm">*{error}</div>}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 font-bold text-white text-base bg-gradient-to-r from-[#445eed] to-[#2d348f] hover:from-[#3916ab] hover:to-[#4962d8] transition-colors rounded-lg shadow-md disabled:opacity-50 disabled:cursor-not-allowed mt-1"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Sending...
                  </div>
                ) : (
                  "Send Reset Link"
                )}
              </button>
            </form>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="text-5xl mb-4">📧</div>
            <h2 className="text-2xl font-extrabold text-[#2d348f] mb-3">Check Your Email</h2>
            <p className="text-sm text-[#837f90] mb-6 leading-relaxed">
              If an account exists for <span className="font-semibold text-[#2d348f]">{email}</span>, a password reset link has been sent. Check your inbox and follow the instructions.
            </p>
            <p className="text-xs text-[#837f90] mb-6">The link will expire in 1 hour.</p>
            <button
              onClick={handleClose}
              className="w-full py-3 font-bold text-white text-base bg-gradient-to-r from-[#445eed] to-[#2d348f] hover:from-[#3916ab] hover:to-[#4962d8] transition-colors rounded-lg shadow-md"
            >
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
